import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { useState } from "react";
import { Lock, Check } from "lucide-react";
import { toast } from "sonner";

export default function Premium() {
  const nav = useNavigate();
  const { user, entitlement, config } = useAuth();
  const [busy, setBusy] = useState(null);

  async function checkout(plan) {
    if (!user) {
      nav("/connexion", { state: { from: "/premium" } });
      return;
    }
    if (!config.configured) {
      toast.error("Paiement non configuré. Contactez l'administrateur.");
      return;
    }
    if (busy) return;
    setBusy(plan);
    try {
      const { data } = await api.post("/subscription/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossible d'initier le paiement.");
      setBusy(null);
    }
  }

  return (
    <div className="animate-fade-in pt-6 pb-6" data-testid="premium-page">
      <div className="rounded-3xl border border-[color:var(--sauge)]/30 bg-gradient-to-br from-[#0D1B2A] via-[#142235] to-[#1B2A3E] p-6">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[color:var(--sauge-light)]">Soft Landing ASMR</p>
        <h1 className="font-serif text-3xl text-[color:var(--ivoire)] mt-2">Passer Premium</h1>
        <p className="mt-3 text-sm text-[color:var(--ivoire-muted)] leading-relaxed">
          Accédez à l'intégralité des séances, des ambiances ASMR et du mode bébé. Vos favoris et préférences sont conservés.
        </p>

        <ul className="mt-6 space-y-2 text-sm text-[color:var(--ivoire)]">
          {[
            "Toutes les séances de 3 à 20 minutes",
            "Toutes les ambiances : bruit rose, brun, pluie, océan",
            "Ambiances complètes pour bébé",
            "Sans publicité",
          ].map((f) => (
            <li key={f} className="flex items-center gap-2">
              <Check size={16} className="text-[color:var(--sauge-light)]" /> {f}
            </li>
          ))}
        </ul>
      </div>

      {entitlement.premium && (
        <div className="mt-6 rounded-2xl border border-[color:var(--sauge)]/40 bg-[color:var(--sauge)]/10 p-4" data-testid="premium-active">
          <p className="text-sm text-[color:var(--sauge-light)] font-medium">Vous êtes déjà Premium.</p>
          {entitlement.ends_at && (
            <p className="text-xs text-[color:var(--ivoire-muted)] mt-1">
              {entitlement.cancel_at_period_end
                ? `Accès jusqu'au ${new Date(entitlement.ends_at).toLocaleDateString("fr-FR")}.`
                : `Prochaine échéance : ${new Date(entitlement.ends_at).toLocaleDateString("fr-FR")}.`}
            </p>
          )}
        </div>
      )}

      {!config.configured && (
        <div className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4" data-testid="premium-not-configured">
          <p className="text-sm text-amber-200 font-medium">Paiement non configuré</p>
          <p className="text-xs text-amber-100/80 mt-2 leading-relaxed">
            Variables serveur requises : STRIPE_SECRET_KEY, STRIPE_WEBHOOK_SECRET, STRIPE_PRICE_MONTHLY_LOOKUP, STRIPE_PRICE_YEARLY_LOOKUP. Puis lancer <span className="font-mono">python setup_stripe.py</span> pour créer les prix.
          </p>
        </div>
      )}

      <div className="mt-8 grid gap-3">
        <button
          data-testid="plan-monthly-btn"
          onClick={() => checkout("monthly")}
          disabled={!!busy || entitlement.premium || !config.configured}
          className="w-full text-left rounded-2xl border border-[color:var(--border)] bg-[#142235]/70 p-5 hover:bg-[#1B2A3E] transition disabled:opacity-60"
        >
          <div className="flex items-baseline justify-between">
            <p className="font-serif text-xl text-[color:var(--ivoire)]">Mensuel</p>
            <p className="font-serif text-2xl text-[color:var(--ivoire)]">6,90 €<span className="text-sm text-[color:var(--ivoire-muted)]"> /mois</span></p>
          </div>
          <p className="mt-1 text-xs text-[color:var(--ivoire-muted)]">Renouvellement automatique. Résiliable à tout moment.</p>
        </button>

        <button
          data-testid="plan-yearly-btn"
          onClick={() => checkout("yearly")}
          disabled={!!busy || entitlement.premium || !config.configured}
          className="relative w-full text-left rounded-2xl border border-[color:var(--sauge)]/50 bg-gradient-to-br from-[#142235]/90 to-[#1B2A3E]/90 p-5 hover:bg-[#1B2A3E] transition disabled:opacity-60"
        >
          <span className="absolute -top-2 right-4 text-[10px] font-mono tracking-widest uppercase text-[color:var(--bg-deep)] bg-[color:var(--sauge-light)] px-2 py-0.5 rounded-full">
            Meilleur tarif
          </span>
          <div className="flex items-baseline justify-between">
            <p className="font-serif text-xl text-[color:var(--ivoire)]">Annuel</p>
            <p className="font-serif text-2xl text-[color:var(--ivoire)]">49 €<span className="text-sm text-[color:var(--ivoire-muted)]"> /an</span></p>
          </div>
          <p className="mt-1 text-xs text-[color:var(--ivoire-muted)]">Soit environ 4,08 €/mois. Facturé en une fois, renouvellement annuel.</p>
        </button>

        <p className="text-[11px] text-[color:var(--ivoire-subtle)] text-center leading-relaxed mt-2">
          Paiement en test Stripe. Aucun encaissement réel dans cette version. Aucune donnée de ressenti ou de bébé n'est transmise à Stripe.
        </p>
      </div>
    </div>
  );
}
